// نسخنة وترحيل التخزين المحلي.
//
// المشكلة التي يحلّها: كل بيانات التطبيق كانت تُكتب في localStorage بلا رقم
// نسخة ولا مسار ترحيل. أي تغيير في شكل البيانات (كإزالة المواقيت المكتوبة
// يدوياً من كائن المدينة) كان سيكسر التطبيق لدى كل مستخدم حالي.
//
// كما أن قائمة الإذاعات كانت تُجمَّد عند أول تشغيل، فلا تصل الإذاعات
// المضافة في التحديثات إلى المستخدمين القدامى أبداً.

import { DEFAULT_RADIOS } from '../data/defaultRadios';

const VERSION_KEY = 'moslam_schema_version';
const CURRENT_VERSION = 3;

/** قراءة JSON بأمان — التخزين قد يكون محظوراً أو تالفاً. */
export function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // ممتلئ أو محظور (وضع التصفح الخاص)
    return false;
  }
}

export function readString(key, fallback = null) {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * يدمج الإذاعات الافتراضية الجديدة مع قائمة المستخدم المحفوظة.
 *
 * يحافظ على تعديلات المستخدم (التفعيل/التعطيل، التحرير، الترتيب)
 * ويضيف ما استُحدث في التحديثات، ويحدّث روابط البث للإذاعات الافتراضية
 * التي لم يعدّلها المستخدم — فالروابط تتغيّر من مزوّد البث أحياناً.
 */
export function mergeStations(saved) {
  if (!Array.isArray(saved) || saved.length === 0) {
    return DEFAULT_RADIOS.map((r) => ({ ...r, isEnabled: true }));
  }

  const savedById = new Map(saved.map((s) => [s.id, s]));
  const merged = [];

  // إذاعات المستخدم المخصصة تبقى كما هي في مقدمة القائمة
  saved.filter((s) => s.isCustom).forEach((s) => merged.push(s));

  for (const def of DEFAULT_RADIOS) {
    const existing = savedById.get(def.id);

    if (!existing) {
      // إذاعة جديدة في هذا التحديث
      merged.push({ ...def, isEnabled: true });
      continue;
    }

    merged.push({
      ...def,
      // تعديلات المستخدم لها الأولوية
      ...(existing.isEdited ? existing : {}),
      // الروابط تأتي من التحديث ما لم يعدّلها المستخدم
      url: existing.isEdited ? existing.url : def.url,
      backupUrl: existing.isEdited ? existing.backupUrl : def.backupUrl,
      isEnabled: existing.isEnabled !== false,
    });
  }

  return merged;
}

/** ترحيل البيانات المحفوظة إلى النسخة الحالية. يُستدعى مرة عند الإقلاع. */
export function runMigrations() {
  let version;
  try {
    version = parseInt(localStorage.getItem(VERSION_KEY) ?? '0', 10) || 0;
  } catch {
    return; // التخزين غير متاح — التطبيق يعمل بالقيم الافتراضية
  }

  if (version >= CURRENT_VERSION) return;

  try {
    // 0 → 1: إزالة مفاتيح المصادقة غير الآمنة من النسخة القديمة
    if (version < 1) {
      localStorage.removeItem('moslam_admin_logged_in');
      localStorage.removeItem('moslam_user_profile');
    }

    // 1 → 2: كائن الموقع لم يعد يحمل مواقيت مكتوبة يدوياً،
    //        فالمواقيت المخزّنة من النسخة القديمة أصبحت بلا معنى.
    if (version < 2) {
      const gps = readJSON('moslam_gps_location', null);
      if (gps && typeof gps === 'object') {
        if ('times' in gps) {
          delete gps.times;
          writeJSON('moslam_gps_location', gps);
        }
      }
      // معرّفات المدن تغيّرت مع توسيع القائمة — تحقّق من صلاحية المحفوظ
      const cityId = readString('moslam_selected_city');
      if (cityId && !/^[a-z]+$/.test(cityId)) {
        localStorage.removeItem('moslam_selected_city');
      }
    }

    // 2 → 3: تنبيهات الأذان لم يعد لها مفتاح تشغيل — تعمل دائماً.
    //        المفتاح المحفوظ صار بلا معنى، ولو بقي لأوهم أنه يعني شيئاً.
    if (version < 3) {
      localStorage.removeItem('moslam_notifications_enabled');
    }

    localStorage.setItem(VERSION_KEY, String(CURRENT_VERSION));
  } catch (err) {
    console.warn('تعذر ترحيل البيانات المحفوظة:', err);
  }
}
